import {
  OWN_SIGNUP_FLOW_COUNTRIES,
  PRODUCER_DEMO_BOOKING_URL,
  IS_PRODUCER_VIEW,
} from "@/constants";
import type { Company, Document } from "@/types";
import { includes } from "lodash-es"; // Import includes
import { computed, Ref, toValue, type MaybeRef } from "vue";
import { useI18n } from "vue-i18n";

export type FollowupActionType =
  | "sign"
  | "register_device"
  | "initial_signup"
  | "international_self_signup"
  | "latvia_manual_authorization"
  | "latvia_sales_followup"
  | "prompt_add_device"
  | "submit_invoice"
  | "none";

export interface FollowupState {
  displayType: "button" | "card" | "none";
  actionType: FollowupActionType; // Describes the underlying logical action

  // Properties for 'button' displayType OR for the button within a 'card'
  text?: string; // Button text
  icon?: string; // Button icon
  link?: string; // Button external link
  route?: object; // Button internal route

  // Properties for 'card' displayType
  cardTitle?: string;
  cardMessage?: string;
}

// Helper function to check for a completed document with a specific flag
function hasSignedDocumentWithFlag(
  documents: Document[] | undefined,
  flagName: keyof Document
): boolean {
  if (!documents) return false;
  return documents.some(
    (doc) => doc.status === "completed" && doc[flagName] === true
  );
}

// Helper function to check for a completed document with a specific contract key
function hasSignedDocumentWithContractKey(
  documents: Document[] | undefined,
  targetKey: string
): boolean {
  if (!documents) return false;
  return documents.some(
    (doc) =>
      doc.status === "completed" && doc.contract_keys?.includes(targetKey)
  );
}

export function computeFollowupState(
  company: Company | undefined,
  accountId: number | undefined,
  hasPendingInvoiceItems: boolean,
  t: (key: string) => string
): FollowupState {
  // 1. Guard clause
  if (!company || accountId === undefined) {
    return { displayType: "none", actionType: "none" };
  }

  // 2. Check for Documents to Sign
  const documentsToSign = company.documents.filter(
    (doc) =>
      doc.status !== "completed" &&
      (doc.document_signers ?? []).some(
        (signer) => signer.account_id === accountId
      )
  );
  if (documentsToSign.length > 0) {
    const firstDoc = documentsToSign[0];
    const signer = (firstDoc.document_signers ?? []).find(
      (s) => s.account_id === accountId
    );
    if (signer?.redirect_url) {
      return {
        displayType: "button",
        actionType: "sign",
        text: t("dashboard.sign_document"),
        icon: "signature",
        link: signer.redirect_url,
      };
    }
  }

  // 3. Trader/Corporate views complete onboarding without followups (except signing documents)
  if (!IS_PRODUCER_VIEW[company.dashboard_view]) {
    return { displayType: "none", actionType: "none" };
  }

  // 4. Check for Generators Needing Registration
  const generatorsToRegister = company.generators.filter(
    (g) => !g.has_registered
  );
  if (generatorsToRegister.length > 0) {
    const firstGen = generatorsToRegister[0];
    return {
      displayType: "button",
      actionType: "register_device",
      text: t("dashboard.continue_registration"),
      icon: "play",
      route: {
        path: "/signup/producer",
        query: {
          step: 2,
          generator_id: firstGen.id,
          company_id: firstGen.company_id,
        },
      },
    };
  }

  const noDevices = (company.total_devices_power ?? 0) === 0;

  // 5. Check for Initial Signup/Authorization that we don't have devices already or no generators or documents yet
  if (
    noDevices &&
    company.generators.length === 0 &&
    company.documents.length === 0
  ) {
    // International leads, and any company outside the countries with their own signup flow, have
    // a company but nothing signed. A sales rep finishes onboarding on a demo call — do NOT push
    // them back into signup; point them at the demo booking page the signup itself ends on.
    if (
      company.feature_flags?.international_self_signup === true ||
      !includes(OWN_SIGNUP_FLOW_COUNTRIES, company.country)
    ) {
      return {
        displayType: "card",
        actionType: "international_self_signup",
        cardTitle: t("followup.international_self_signup.title"),
        cardMessage: t("followup.international_self_signup.message"),
        text: t("followup.international_self_signup.book_demo"),
        icon: "calendar",
        link: PRODUCER_DEMO_BOOKING_URL,
      };
    }

    const baseState: FollowupState = {
      displayType: "button",
      actionType: "initial_signup",
      icon: "play",
      text: "",
      route: {},
    };

    // Finland's two paths sign different documents, so the producer picks one again.
    if (company.country === "FI") {
      return {
        ...baseState,
        text: t("dashboard.continue_registration"),
        route: {
          path: "/signup/producer",
          query: { country: "fi", step: 1, company_id: company.id },
        },
      };
    }

    return {
      ...baseState,
      text: t("dashboard.companyData.continue_authorization_creation"),
      route: {
        path: "/signup/producer",
        query: {
          step: 2,
          company_id: company.id,
          addAuthorizationAgreement: "true",
        },
      },
    };
  }

  // 6. Check for Pending Invoice Items (Submit Invoice action)
  if (hasPendingInvoiceItems) {
    return {
      displayType: "button",
      actionType: "submit_invoice",
      text: t("dashboard.companyData.submit_invoice"),
      icon: "file-invoice-dollar",
      route: {
        path: "/dashboard",
        query: {
          company_id: company.id,
          tab: "invoice-history",
          "invoice-management-active-tab": "submit-invoice",
        },
      },
    };
  }

  // 7. Contract-Signed Dependent Actions
  const isContractSigned =
    company.has_registered ||
    hasSignedDocumentWithFlag(company.documents, "has_soldera_contract");

  if (isContractSigned && noDevices) {
    // 7a. Latvia Manual Authorization Needed (Card)

    if (company.country === "LV") {
      // Turns out the Latvian auth form is not marked as an authorization contract,
      // so we need to check for it separately
      // this is a hacky workaround but works for now. See:
      // https://www.notion.so/solidworld/Latvian-authorization-flow-has-some-data-quality-issues-1ec259ad6d998084b11ff14169152f6e?pvs=4
      if (
        hasSignedDocumentWithContractKey(
          company.documents,
          "lv_account_holder_registration_form_auth"
        )
      ) {
        // Manual authorization into Grexel is needed
        return {
          displayType: "card",
          actionType: "latvia_manual_authorization",
          cardTitle: t("followup.latvia_manual_auth.title"),
          cardMessage: t("followup.latvia_manual_auth.message"),
          text: t("followup.latvia_manual_auth.button"),
          icon: "file-pdf",
          link: "https://soldera-public-statics.s3.eu-west-1.amazonaws.com/help/grexel_latvia_instructions.pdf",
        };
      } else {
        // Our sales team needs to follow up
        return {
          displayType: "card",
          actionType: "latvia_sales_followup",
          cardTitle: t("followup.latvia_sales_followup.title"),
          cardMessage: t("followup.latvia_sales_followup.message"),
          text: t("followup.latvia_sales_followup.button"),
          icon: "plus",
          route: { name: "ProducerSignUp" },
        };
      }
    }

    // 7b. Prompt Add Device (Card) - Fallback if contract signed, no devices, and not Latvia case
    const buttonTextKey = includes(["EE", "LV", "LT", "SE"], company.country)
      ? "dashboard.companyData.add_companies_or_devices_while_waiting"
      : "dashboard.companyData.add_companies_while_waiting";
    return {
      displayType: "card",
      actionType: "prompt_add_device",
      cardTitle: t("dashboard.companyData.thank_you_for_joining"),
      cardMessage: t("dashboard.companyData.thank_you_message"),
      text: t(buttonTextKey),
      icon: "plus",
      route: { name: "ProducerSignUp" },
    };
  }

  // 8. No Action Needed (Fallback for all other scenarios, e.g., contract not signed)
  return { displayType: "none", actionType: "none" };
}

export function useFollowupState(
  companyInput: MaybeRef<Company | undefined>,
  accountIdInput: MaybeRef<number | undefined>,
  hasPendingInvoiceItemsInput?: MaybeRef<boolean>
): { followupState: Ref<FollowupState> } {
  const { t } = useI18n();

  const followupState = computed((): FollowupState => {
    const company = toValue(companyInput);
    const accountId = toValue(accountIdInput);
    const hasPendingInvoiceItems =
      toValue(hasPendingInvoiceItemsInput) ?? false;

    return computeFollowupState(company, accountId, hasPendingInvoiceItems, t);
  });

  return {
    followupState,
  };
}
